import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowBendDownRightIcon,
  ArrowDownRightIcon,
  PauseIcon,
  PlayIcon,
} from '@phosphor-icons/react';
import {
  type BlockReason,
  type CardId,
  deriveTaskDisplayStatus,
  HttpError,
  type TaskId,
  type TaskWithCard,
} from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { boardRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { EmptyHint, useOrgContext, useSnackbar } from '@web/ui/core';
import { Button, IconButton, TaskStatusIcon } from '@web/ui/core/atoms';
import { fireAndForget, twcn } from '@web/ui/core/utils';

import { BlockReasonForm } from './components/block_reason_form';
import { useCardRoutes } from './hooks/use_card_routes';
import { useCardTasks } from './hooks/use_card_tasks';

/**
 * Task detail — rendered through `CardDetail`'s `<Outlet />`. Owns its own
 * aside, docked flush to the left of the card drawer (it provides no scrim;
 * the card drawer's backdrop covers both). Closing returns to the card Tasks
 * tab without the task child, keeping the `?view` filter.
 */
export function TaskDetail(): React.JSX.Element {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const routes = useCardRoutes();
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [blocking, setBlocking] = useState(false);

  const cardId = params.cardId as CardId | undefined;
  const taskId = params.taskId as TaskId | undefined;

  const { tasks } = useCardTasks(cardId as CardId);

  // Resolve the task via the `task.complete` CardEvent. Invalidate the card's
  // task list and the boards so the task moves to the `resolved` column.
  const resolve = useMutation({
    mutationFn: (vars: { cardId: CardId; taskId: TaskId }) =>
      boardRepository.dispatchCardEvent(orgId, vars.cardId, {
        event: { type: 'task.complete', taskId: vars.taskId },
      }),
    onSuccess: (_data, vars) => {
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.tasks(orgId, vars.cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
    },
  });

  // Claim the task via the `task.claim` CardEvent. `newAssigneeAgent` is
  // omitted so the backend assigns it to the requesting user. Same
  // invalidations as `resolve` so `assignedToMe` and the boards refresh.
  const claim = useMutation({
    mutationFn: (vars: { cardId: CardId; taskId: TaskId }) =>
      boardRepository.dispatchCardEvent(orgId, vars.cardId, {
        event: { type: 'task.claim', taskId: vars.taskId, reason: 'escalation' },
      }),
    onSuccess: (_data, vars) => {
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.tasks(orgId, vars.cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
    },
  });

  const block = useMutation({
    mutationFn: (vars: { cardId: CardId; taskId: TaskId; blockReason: BlockReason }) =>
      boardRepository.dispatchCardEvent(orgId, vars.cardId, {
        event: { type: 'task.block', taskId: vars.taskId, blockReason: vars.blockReason },
      }),
    onSuccess: (_d, vars) => {
      setBlocking(false);
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.tasks(orgId, vars.cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
    },
    onError: (err: unknown) => {
      showSnackbar({
        severity: 'error',
        message:
          err instanceof HttpError || err instanceof Error
            ? err.message
            : t('board.taskDrawer.block.error'),
      });
    },
  });

  const unblock = useMutation({
    mutationFn: (vars: { cardId: CardId; taskId: TaskId }) =>
      boardRepository.dispatchCardEvent(orgId, vars.cardId, {
        event: { type: 'task.unblock', taskId: vars.taskId },
      }),
    onSuccess: (_d, vars) => {
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.cards.tasks(orgId, vars.cardId) }),
      );
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.boards.all }));
    },
    onError: (err: unknown) => {
      showSnackbar({
        severity: 'error',
        message:
          err instanceof HttpError || err instanceof Error
            ? err.message
            : t('board.taskDrawer.unblock.error'),
      });
    },
  });

  if (!cardId || !taskId) return <></>;

  const task = tasks.find((x) => x.id === taskId);

  const closeInline = (): void => {
    fireAndForget(navigate(routes.tab('tasks')));
  };

  return (
    <aside
      className="flex w-drawer-w shrink-0 flex-col overflow-hidden bg-surface border-l border-border-soft shadow-overlay animate-drawer-in"
      data-testid="task-drawer"
    >
      {task ? (
        <InlineTaskPanel
          task={task}
          onClose={closeInline}
          onResolve={() => {
            resolve.mutate({ cardId, taskId: task.id });
          }}
          resolving={resolve.isPending}
          onClaim={() => {
            claim.mutate({ cardId, taskId: task.id });
          }}
          claiming={claim.isPending}
          blockOpen={blocking}
          onOpenBlock={() => {
            setBlocking(true);
          }}
          onBlock={(reason) => {
            block.mutate({ cardId, taskId: task.id, blockReason: reason });
          }}
          blockPending={block.isPending}
          onUnblock={() => {
            unblock.mutate({ cardId, taskId: task.id });
          }}
          unblocking={unblock.isPending}
        />
      ) : (
        <div className="flex-1 overflow-auto px-5 py-6">
          <EmptyHint testId="task-drawer-missing">{t('board.taskDrawer.notFound')}</EmptyHint>
        </div>
      )}
    </aside>
  );
}

function InlineTaskPanel({
  task,
  onClose,
  onResolve,
  resolving,
  onClaim,
  claiming,
  blockOpen,
  onOpenBlock,
  onBlock,
  blockPending,
  onUnblock,
  unblocking,
}: Readonly<{
  task: TaskWithCard;
  onClose: () => void;
  onResolve: () => void;
  resolving: boolean;
  onClaim: () => void;
  claiming: boolean;
  blockOpen: boolean;
  onOpenBlock: () => void;
  onBlock: (reason: BlockReason) => void;
  blockPending: boolean;
  onUnblock: () => void;
  unblocking: boolean;
}>): React.JSX.Element {
  const canBlock = task.status === 'running';
  const canUnblock = task.status === 'blocked';
  const showActions = hasTaskActions(task);

  return (
    <>
      <header className="flex items-center gap-3 border-b border-border-soft px-5 py-3">
        <IconButton
          icon="close"
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label={t('board.taskDrawer.closeAria')}
          data-testid="task-drawer-close"
        />
        <TaskStatusIcon status={deriveTaskDisplayStatus(task)} framed size={18} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-base font-medium text-foreground">{task.description}</span>

          <div className="flex items-center gap-1">
            <span className="font-mono text-tiny uppercase tracking-eyebrow text-faint-foreground">
              T-{task.id.slice(0, 6)} ·
            </span>
            <span className="text-meta text-muted-foreground">
              {t('board.taskDrawer.kind')} · #{task.card.seq}
            </span>
          </div>
        </div>
      </header>

      {showActions ? (
        <div
          data-testid="task-actions"
          className="flex items-center justify-end gap-2 border-b border-border-soft px-5 py-2"
        >
          <TaskPrimaryAction
            task={task}
            onResolve={onResolve}
            resolving={resolving}
            onClaim={onClaim}
            claiming={claiming}
          />
          {canBlock && !blockOpen ? (
            <Button variant="outline" size="sm" onClick={onOpenBlock}>
              <PauseIcon size={14} weight="bold" aria-hidden />
              {t('board.taskDrawer.action.block')}
            </Button>
          ) : null}
          {canUnblock ? (
            <Button
              variant="primary"
              size="sm"
              disabled={unblocking}
              aria-disabled={unblocking}
              onClick={onUnblock}
            >
              <PlayIcon size={14} weight="bold" aria-hidden />
              {t('board.taskDrawer.action.unblock')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {canBlock && blockOpen ? (
        <div className="border-b border-border-soft px-5 py-3">
          <BlockReasonForm onSubmit={onBlock} pending={blockPending} />
        </div>
      ) : null}

      <div className="flex-1 overflow-auto px-5 py-6">
        <SectionHead label={t('board.taskDrawer.execution')} />
        <Kv>
          <Row k={t('board.taskDrawer.field.assignedTo')} v={assignedToLabel(task)} />
          <Row k={t('board.taskDrawer.field.agent')} v={task.agentDisplayName ?? '—'} last />
        </Kv>

        {task.errorCode || task.blockReason ? (
          <>
            <SectionHead label={t('board.taskDrawer.context')} top />
            <p className="m-0 mt-2 font-mono text-meta text-muted-foreground whitespace-pre-wrap break-words">
              {task.errorCode ?? blockText(task)}
            </p>
          </>
        ) : null}
      </div>
    </>
  );
}

function assignedToLabel(task: TaskWithCard): string {
  if (!task.assigneeAgent) return t('board.taskDrawer.none');
  return task.assigneeAgent.kind === 'operator'
    ? t('board.taskDrawer.assignedHuman')
    : t('board.taskDrawer.assignedAi');
}

function hasTaskActions(task: TaskWithCard): boolean {
  if (task.status === 'running' || task.status === 'blocked') return true;
  return task.status === 'pending' && !task.assignedToMe;
}

function TaskPrimaryAction({
  task,
  onResolve,
  resolving,
  onClaim,
  claiming,
}: Readonly<{
  task: TaskWithCard;
  onResolve: () => void;
  resolving: boolean;
  onClaim: () => void;
  claiming: boolean;
}>): React.JSX.Element | null {
  if (task.assignedToMe && task.status === 'running') {
    return (
      <Button
        variant="primary"
        size="sm"
        disabled={resolving}
        aria-disabled={resolving}
        onClick={onResolve}
      >
        <ArrowBendDownRightIcon size={14} weight="bold" aria-hidden />
        {t('board.taskDrawer.action.resolve')}
      </Button>
    );
  }
  if (task.assignedToMe || task.status === 'done' || task.status === 'failed') return null;
  return (
    <Button
      variant="primary"
      size="sm"
      disabled={claiming}
      aria-disabled={claiming}
      onClick={onClaim}
    >
      <ArrowDownRightIcon size={14} weight="bold" aria-hidden />
      {t('board.taskDrawer.action.assume')}
    </Button>
  );
}

function blockText(task: TaskWithCard): string {
  if (task.status !== 'blocked' || !task.blockReason) return '';
  const br = task.blockReason;
  if (br.kind === 'human_in_the_loop') return t('board.drawer.tasks.block.hitl', { gate: br.gate });
  if (br.kind === 'awaiting_input') {
    return br.source === 'client'
      ? t('board.drawer.tasks.block.awaitingClient')
      : t('board.drawer.tasks.block.awaitingVendor');
  }
  return t('board.drawer.tasks.block.scheduled');
}

function SectionHead({
  label,
  top,
}: Readonly<{ label: string; top?: boolean }>): React.JSX.Element {
  return (
    <div
      className={twcn(
        'font-mono text-tiny uppercase tracking-eyebrow text-muted-foreground',
        top && 'mt-6',
      )}
    >
      {label}
    </div>
  );
}

function Kv({ children }: Readonly<{ children: React.ReactNode }>): React.JSX.Element {
  return (
    <div className="mt-2 rounded-md border border-border-soft bg-surface">
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function Row({
  k,
  v,
  last,
}: Readonly<{ k: string; v: React.ReactNode; last?: boolean }>): React.JSX.Element {
  return (
    <div
      className={twcn('flex items-center gap-3 px-3 py-2', !last && 'border-b border-border-soft')}
    >
      <span className="w-32 flex-shrink-0 text-meta text-muted-foreground">{k}</span>
      <span className="flex-1 text-base text-foreground min-w-0 truncate">{v}</span>
    </div>
  );
}
